import { FlussonicHlsVideoPlayer } from '@repo/hls-flussonic-video-player';
import type {
  FlussonicHlsVideoPlayerProps,
  FlussonicHlsVideoPlayerSettingsChangeHandler,
  HlsPlaybackMode,
} from '@repo/hls-flussonic-video-player';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';

import type { CameraState, VideoBroadcastWidgetState } from '../store/VideoBroadcastWidgetState';

export type SyncedHlsVideoPlayerProps = Omit<
  FlussonicHlsVideoPlayerProps,
  | 'autoplay'
  | 'onArchiveUnavailable'
  | 'onLiveStatusChange'
  | 'onLiveModeRequest'
  | 'onPlayerSettingsChange'
  | 'onPlaybackTimeChange'
  | 'onTimelineSeek'
  | 'playerSettings'
  | 'playbackBounds'
  | 'playbackState'
  | 'sourceUrl'
> & {
  camera: CameraState;
  widgetState: VideoBroadcastWidgetState;
};

export const SyncedHlsVideoPlayer = observer(function SyncedHlsVideoPlayer({
  camera,
  widgetState,
  ...playerProps
}: SyncedHlsVideoPlayerProps) {
  const handleLiveModeRequest = useCallback(() => {
    widgetState.setLiveMode();
  }, [widgetState]);

  const handleLiveStatusChange = useCallback(
    (status: { liveAvailable: boolean | null; liveEdgeMs: number | null }) => {
      widgetState.setCameraLiveStatus(camera, status);
    },
    [camera, widgetState],
  );

  const handleArchiveUnavailable = useCallback(
    (timeMs: number) => {
      widgetState.confirmPlayerArchiveUnavailable(camera, timeMs);
    },
    [camera, widgetState],
  );

  const handlePlaybackTimeChange = useCallback(
    (timeMs: number, mode: HlsPlaybackMode) => {
      widgetState.setPlayerPlaybackTime(camera, timeMs, 'player', mode);
    },
    [camera, widgetState],
  );

  const handleTimelineSeek = useCallback(
    (timeMs: number) => {
      widgetState.setPlayerPlaybackTime(camera, timeMs, 'timeline', 'archive');
    },
    [camera, widgetState],
  );

  const handlePlayerSettingsChange = useCallback<FlussonicHlsVideoPlayerSettingsChangeHandler>(
    (settings, options) => {
      widgetState.setPlayerSettings(camera, settings, options);
    },
    [camera, widgetState],
  );

  const initialPlaybackState = widgetState.getPlayerInitialPlaybackState(camera);
  const displayTimeOffsetMs = playerProps.displayTimeOffsetMs ?? widgetState.timeZonesStore.current;
  const playerSettings = widgetState.getPlayerSettings(camera);
  const linkedVideoSyncEnabled = widgetState.linkedVideoSyncEnabled;
  const playbackState = linkedVideoSyncEnabled
    ? {
        mode: widgetState.playbackMode,
        revision: widgetState.playerRemountKey,
        timeMs: widgetState.playbackTimeMs,
      }
    : undefined;
  const playbackBounds =
    widgetState.videoInterval != null
      ? { endMs: widgetState.videoInterval.toMs, locked: true, startMs: widgetState.videoInterval.fromMs }
      : null;
  const autoplay = widgetState.shouldAutoplayCamera();
  const sourceUrl = camera.data.broadcastLink;

  return (
    <FlussonicHlsVideoPlayer
      {...playerProps}
      autoplay={autoplay}
      displayTimeOffsetMs={displayTimeOffsetMs}
      initialPlaybackState={initialPlaybackState}
      locale={widgetState.appParams.locale}
      onArchiveUnavailable={handleArchiveUnavailable}
      onLiveModeRequest={widgetState.linkedVideoSyncEnabled ? handleLiveModeRequest : undefined}
      onLiveStatusChange={handleLiveStatusChange}
      onPlayerSettingsChange={handlePlayerSettingsChange}
      onPlaybackTimeChange={handlePlaybackTimeChange}
      onTimelineSeek={widgetState.linkedVideoSyncEnabled ? handleTimelineSeek : undefined}
      playerSettings={playerSettings}
      playbackBounds={playbackBounds}
      playbackState={playbackState}
      sourceUrl={sourceUrl}
    />
  );
});
